import { Alert } from "../../../../../components/alert/Alert";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../../../hooks/api/v2/apiV2Request";
import { useForm } from "@tanstack/react-form";
import { Button, InputCurrency, InputText } from "@sellar-io/components";
import { useSellarImportCustomFeeAdd } from "../hooks/useSellarImportCustomFeeAdd";
import { z } from "zod";

const formSchema = z.object({
  name: z.string().min(1),
  amount: z.number().min(0),
});

export const AddCustomFeeDialog = ({
  active,
  onDismiss,
  payoutId,
}: {
  active: boolean;
  onDismiss: () => void;
  payoutId: number;
}) => {
  const addCustomFeeMutation = useSellarImportCustomFeeAdd({
    payoutId,
  });

  const form = useForm({
    defaultValues: {
      name: "",
      amount: 0,
    },
    onSubmit: ({ value }) => {
      const data = formSchema.parse(value);
      const amountPence = Math.round(data.amount * 100);

      addCustomFeeMutation.mutate(
        {
          name: data.name,
          amount: amountPence,
        },
        {
          onSuccess: () => handleDismiss(),
        },
      );
    },
  });

  const handleDismiss = () => {
    form.reset();
    onDismiss();
  };

  return (
    <Dialog active={active} onDismiss={handleDismiss}>
      <DialogNavigation
        title={"Add custom fee"}
        description={"Add a custom fee to this payout"}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={handleDismiss}
      />
      <form
        onSubmit={(event) => {
          event.preventDefault();
          form.handleSubmit();
        }}
      >
        <DialogContent>
          {addCustomFeeMutation.isError ? (
            <Alert
              type={"error"}
              message={
                apiV2AssertError(addCustomFeeMutation.error)?.response?.data
                  .message ?? "Unknown error occurred."
              }
            />
          ) : null}

          <form.Field name="name">
            {({ state, handleBlur, handleChange }) => (
              <>
                <InputLabel>Fee Name</InputLabel>
                <InputContainer>
                  <InputText
                    value={state.value}
                    onBlur={handleBlur}
                    onChange={(e) => handleChange(e.target.value)}
                    placeholder="e.g. Special Handling Fee"
                    required
                  />
                </InputContainer>
              </>
            )}
          </form.Field>

          <form.Field name="amount">
            {({ state, handleBlur, handleChange }) => (
              <>
                <InputLabel>Amount (£)</InputLabel>
                <InputContainer>
                  <InputCurrency
                    showPrefix
                    allowNegative
                    value={state.value}
                    onBlur={handleBlur}
                    onChange={(e) => handleChange(Number(e.target.value))}
                    required
                    min={0}
                  />
                </InputContainer>
              </>
            )}
          </form.Field>
        </DialogContent>
        <DialogCTA>
          <Button loading={addCustomFeeMutation.isPending} type="submit">
            Add Fee
          </Button>
        </DialogCTA>
      </form>
    </Dialog>
  );
};
